import { UserService } from 'go-modules/models/user/user.service';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { MessageModal } from 'go-modules/modals/message/modal.factory';
import { SessionListDataSource } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import {
	VideoDetailsDialogComponent
} from '@go-modules/components/dialogs/video-details-dialog/video-details-dialog.component';
import { combineLatest, from, Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { SelectedService } from 'go-modules/services/selected/selected.service';
import { VideoShareDialogService } from '@go-modules/components/dialogs/video-share-dialog/video-share-dialog.service';

/* @ngInject */
export const sessionEditorPanelController = function (
	$scope,
	userService: UserService,
	User,
	Group,
	Session,
	$filter,
	confirmModal,
	$document: ng.IDocumentService,
	$timeout: ng.ITimeoutService,
	messageModal: MessageModal,
	$log: ng.ILogService,
	$translate: ng.translate.ITranslateService,
	ngxSessionListDataSource: SessionListDataSource,
	ngxGoModalService: GoModalService,
	ngxVideoShareDialogService: VideoShareDialogService,
	selectedService: SelectedService,
	$injector
) {

	const vm = this;
	let previousScore;
	vm.saving = false;
	vm.showAddPresenterInput = false;
	vm.currentUserId = userService.currentUser.user_id;
	vm.selectedUser = null;
	vm.filteredUsers = [];
	vm.activeTab = vm.activeTab;
	vm.isLoading = true;

	vm.$onInit = () => {
		const mentoredUserIds$: Observable<number[]> = selectedService.getMentoredUserIds$();
		const users$: Observable<any> = from(User.getByGroup({
			groupId: vm.group.getId()
		}).$promise);
		const orgSettings$ = from(Group.getOrgSettings({group_id: vm.group.group_id}));

		vm.usersSubscription = combineLatest([users$, mentoredUserIds$, orgSettings$])
			.pipe(
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				tap(([users, mentoredUserIds, orgSettings]) => {
					vm.orgSettings = orgSettings;
				}),
				map(([users, mentoredUserIds]) => {
					if (mentoredUserIds && mentoredUserIds.length > 0) {
						return users.filter((user) => user.user_id === vm.currentUserId
							|| mentoredUserIds.includes(user.getId())
						);
					}

					return users;
				})
			)
			.subscribe((filteredUsers) => {
				$scope.$evalAsync(() => {
					vm.users = filteredUsers;
				});
				vm.isLoading = false;
			});

		previousScore = vm.session.score;
		vm.originalSourceMedia = vm.session.source_media;
		vm.searchOptions = vm.getSearchOptions();

		$scope.$on('$destroy', () => {
			vm.usersSubscription?.unsubscribe();
		});
	};

	vm.openMediaDetailsModal = (event) => {
		event.stopPropagation();

		return ngxGoModalService.open(VideoDetailsDialogComponent, true, {
			data: {
				session: vm.session
			}
		});
	};

	vm.canRemovePresenter = (presenter) => {
		return vm.group.hasInstructorRole(true) || presenter.user_id !== vm.currentUserId;
	};

	vm.removePresenter = (presenter) => {
		const index = vm.session.presenters.findIndex((p) => {
			return parseInt(p.user_id, 10) === parseInt(presenter.user_id, 10);
		});

		vm.session.presenters.splice(index, 1);
	};

	vm.displayPresenterEmail = (user) => {
		return vm.group.hasInstructorRole(true) ? user.email : '—';
	};

	vm.displayGuestReviewEmail = (guest) => {
		if (vm.group.hasInstructorRole(true)) {
			return guest.email;
		}
		return (guest.uuid && guest.created_by === vm.currentUserId) ? guest.email : '—';
	};

	vm.onSelect = (item) => {
		if (item && item.user_id) {
			vm.session.presenters.push(item);
			vm.showAddPresenterInput = false;
		}
	};

	vm.save = ($event) => {
		let savePromise;
		vm.saving = true;

		// When the score has changed, post or retract the grade
		// depending on whether the score value is falsy or not.
		if (vm.session.score !== previousScore) {
			if (vm.session.score) {
				vm.session.postGrade(vm.session.score);
				vm.activity.num_graded = vm.activity.num_graded + 1;
			} else {
				vm.session.retractGrade();
				vm.activity.num_graded = vm.activity.num_graded - 1;
			}
		}

		previousScore = vm.session.score;
		$event.preventDefault();

		if (!vm.session.source_media && vm.originalSourceMedia) {
			savePromise = vm.session.deleteSlideDeck();
		} else {
			savePromise = vm.session.save();
		}

		savePromise.then(() => {
			if (vm.session.activity_id !== vm.activity.activity_id) {
				ngxSessionListDataSource.removeSession(Session.model(vm.session));
			}
			vm.saving = false;
			vm.$close(vm.session);
		});
	};

	vm.showNewPresenterInput = () => {
		vm.showAddPresenterInput = true;

		$timeout(() => {
			($document[0].querySelector('.session-editor-panel input.typeahead-input') as HTMLInputElement).focus();
		});
	};

	vm.getReviewers = () => {
		return vm.session.guest_reviewers.concat(vm.session.internal_reviewers);
	};

	/**
	 * Filter users given a query string
	 *
	 * @param query
	 * @returns {Array}
	 */
	vm.filterUsers = () => {
		const filtered = vm.users.map((users) => {
			if (!vm.group.hasInstructorRole(true)) {
				users.email = '';
			}
			return users;
		});
		return $filter('filter')(filtered, notSelectedFilter());
	};

	vm.getSearchOptions = () => {
		const options = ['fullname'];
		if (vm.group.hasInstructorRole(true)) {
			options.push('email');
		}
		return options;
	};

	vm.restoreGuest = (guest) => {
		confirmModal.open({
			modalData: {
				title: 'session-editor-panel_guest-reviewers-restore',
				message: $translate.instant('session-editor-panel_guest-reviewers-restore-message',
					{ name: guest.user == null ? guest.email : `${guest.user.first_name} ${guest.user.last_name}`}),
				yes: 'common_restore'
			}
		}).result.then(() => {
			const data = { session_id: vm.session.session_id, guest_id: guest.id };
			return Session.restoreGuestReviewer(data, data)
				.$promise.then(() => {
					guest.deleted_at = null;
					const guestReviewers = vm.session.guest_reviewers.map((reviewer) => reviewer.id === guest.id
						? {...reviewer, deleted_at: null}
						: reviewer
					);
					vm.session = Session.model({...vm.session, guest_reviewers: guestReviewers}, { skipCache: true });
				}).catch((err) => {
					$log.error(err);

					if (err && err.status === 409 &&
						err.data.message.includes('This video has reached the maximum amount of guest reviewers')
					) {
						messageModal.open({
							modalData: {
								title: 'modal-video-share_restore-fail_title',
								message: $translate.instant('modal-video-share_share-fail-max-users_message',
									{ limit: clientSettings.SessionShareGuestReviewerLimit })
							}
						});
					}
				});
		}).catch((msg) => $log.info(msg));
	};

	vm.shouldShowGuestReviewersSection = () => {
		return $injector.get('ngxSessionService').hasVideoSharePermission(vm.session);
	};

	vm.shareSession = () => {
		ngxVideoShareDialogService.open({
			orgSettings: vm.orgSettings,
			sessionId: vm.session.session_id
		}).subscribe((res) => {
			// Check if there are changes in the number of reviewers.
			// Possible case is when user removes some reviewers then closes the modal.
			const hasReviewerChange = res.session
				? extractReviewers(vm.session).length !== extractReviewers(res.session).length
				: false;

			if ((res && !res.dismissed) || hasReviewerChange) {
				Session.get({
					'session_id': vm.session.session_id,
					'with[]': ['externalSessionUsers', 'externalSessionUsers.user', 'internalReviewers']
				}).$promise.then((session) => {
					vm.session = Session.model({...session}, { skipCache: true });
				});
			}
		});
	};

	vm.expireAccess = () => {
		confirmModal.open({
			modalData: {
				title: 'modal-expire-public-link-access_title',
				message: 'modal-expire-public-link-access_text',
				yes: 'session-editor-panel_expire-link',
				no: 'common_cancel'
			}
		}).result.then(() => {
			return Session.expirePublicLinkAccess({ session_id: vm.session.session_id }).$promise.then(() => {
				// Guests with a null uuid have access from the public link and should be removed from the list
				vm.session.guest_reviewers = vm.session.guest_reviewers.filter((guest) => guest.uuid !== null);
				vm.session = Session.model({...vm.session}, { skipCache: true });

			}).catch((err) => $log.error(err));
		}).catch((msg) => $log.info(msg));
	};

	vm.deleteReviewer = (guest) => {
		if (guest.hasOwnProperty('uuid')) {
			guest.deleted_at = new Date();
		} else {
			vm.session.internal_reviewers = vm.session.internal_reviewers.filter((reviewer) => {
				return reviewer.user_id !== guest.user_id;
			});
		}
	};

	/**
	 * Not selected filter
	 */
	function notSelectedFilter () {
		return function filterFn (user) {
			return !vm.session.isPresenter(user);
		};
	}

	function extractReviewers (session) {
		return session.internal_reviewers
			.concat(session.guest_reviewers)
			.filter((reviewer) => reviewer.deleted_at === null || !reviewer.hasOwnProperty('deleted_at'));
	}
};
